'use client'

import { cn } from '@/lib/utils'

interface YouTubeBlockContent {
  videoId: string
  title?: string
}

interface YouTubeBlockProps {
  content: YouTubeBlockContent
  className?: string
}

// Extract video ID from various YouTube URL formats
export function extractYouTubeVideoId(input: string): string | null {
  // If it's already just a video ID (11 characters, alphanumeric with - and _)
  if (/^[\w-]{11}$/.test(input)) {
    return input
  }

  // Try to parse as URL
  try {
    const url = new URL(input)
    
    // youtube.com/watch?v=VIDEO_ID
    if (url.hostname.includes('youtube.com')) {
      const videoId = url.searchParams.get('v')
      if (videoId) return videoId
      
      // youtube.com/embed/VIDEO_ID
      const pathMatch = url.pathname.match(/\/embed\/([^/?]+)/)
      if (pathMatch) return pathMatch[1]
    }
    
    // youtu.be/VIDEO_ID
    if (url.hostname === 'youtu.be') {
      return url.pathname.slice(1).split('?')[0]
    }
  } catch {
    // Not a valid URL
  }

  return null
}

export function YouTubeBlock({ content, className }: YouTubeBlockProps) {
  if (!content.videoId) return null

  const videoId = extractYouTubeVideoId(content.videoId) || content.videoId

  return (
    <div className={cn('space-y-2', className)}>
      {content.title && (
        <h3 className="text-lg font-semibold text-white">{content.title}</h3>
      )}
      <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-zinc-800">
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${videoId}`}
          title={content.title || 'YouTube video'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        />
      </div>
    </div>
  )
}



